import { Component, HostListener, OnInit } from '@angular/core';
import { DataType } from '../../../shared/ontology/model/data-type';
import { JhiEventManager } from '../../../shared/core-utils';
import { ScaleService } from '../../../shared/ontology/service/scale.service';
import { OntologyContext } from '../../ontology.context';
import { ScaleDetails } from '../../../shared/ontology/model/scale-details';
import { PopupService } from '../../../shared/modal/popup.service';
import { finalize } from 'rxjs/internal/operators/finalize';
import { AlertService } from '../../../shared/alert/alert.service';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { VariableDetails } from '../../../shared/ontology/model/variable-details';
import { VariableService } from '../../../shared/ontology/service/variable.service';
import { PropertyService } from '../../../shared/ontology/service/property.service';
import { MethodService } from '../../../shared/ontology/service/method.service';
import { PropertyDetails } from '../../../shared/ontology/model/property-details';
import { MethodDetails } from '../../../shared/ontology/model/method-details';
import { VariableType } from '../../../shared/ontology/model/variable-type';
import { OntologyService } from '../../../shared/ontology/service/ontology.service';
import { DataTypeEnum } from '../../../shared/ontology/data-type.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProgramService } from '../../../shared/program/service/program.service';
import { ProgramFavoriteTypeEnum } from '../../../shared/program/model/program-favorite-type.enum';
import { ProgramFavoriteRequest } from '../../../shared/program/model/program-favorite-add-request.model';
import { HttpResponse } from '@angular/common/http';
import { ProgramFavorite } from '../../../shared/program/model/program-favorite.model';
import { canEditFieldByMetadata } from '../../shared/utils/ontology-editing.util';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-variable-dialog',
    templateUrl: './variable-dialog.component.html',
    styleUrls: ['./../../ontology-manager.scss']
})
export class VariableDialogComponent implements OnInit {
    protected readonly NAME_TEXT_LIMIT = 32;
    protected readonly ALIAS_TEXT_LIMIT = 32;
    protected readonly DESCRIPTION_TEXT_LIMIT = 1024;
    protected readonly MIN_RANGE = -999999999;
    protected readonly MAX_RANGE = 999999999;
    protected readonly SPECIAL_CHARACTERS = '^[^<>?|]*$';
    protected readonly NAME_ALIAS_PATTERN = /^[\p{L}][\p{L}0-9_]*$/u;
    protected readonly canEditFieldByMetadata = canEditFieldByMetadata;

    protected readonly ModeView = ModeView;
    protected readonly NUM_EDITABLE_FIELDS = 6;
    protected readonly ObsoleteEnum = ObsoleteEnum;
    protected readonly ANALYSIS_VARIABLE_TYPE = ['1801', '1811'];
    protected readonly ALIAS_VARIABLE_ALLOWED = ['1807', '1808', '1802', '1813', '1814', '1815', '1816'];

    dataTypes: DataType[];
    isLoading: boolean;
    showRangeWidget: boolean;
    showCategoricalWidget: boolean;
    modeView!: ModeView;
    obsoleteVariable!: ObsoleteEnum;
    variableDetails!: VariableDetails;
    variableDetailsBackup!: VariableDetails;
    properties: PropertyDetails[] = [];
    methods: MethodDetails[] = [];
    scales: ScaleDetails[] = [];
    variableTypes: VariableType[] = [];
    showAliasField: boolean = false;
    showParenVariable: boolean = false;
    invalidAnalysisScaleConbination: boolean = false;
    isTogglingFavoriteProgram: boolean;
    variableId: number | null = null;

    constructor(public activeModal: NgbActiveModal,
                private modalService: NgbModal,
                private router: Router,
                private eventManager: JhiEventManager,
                private variableService: VariableService,
                private propertyService: PropertyService,
                private methodService: MethodService,
                private scaleService: ScaleService,
                private programService: ProgramService,
                private alertService: AlertService,
                private ontologyService: OntologyService,
                private ontologyContext: OntologyContext,
                private translateService: TranslateService) {
        this.variableDetails = new VariableDetails();
    }

    ngOnInit(): void {
        this.resetWidgets();
        this.modeView = this.variableId ? ModeView.VIEW : ModeView.CREATE;
        if (this.modeView === ModeView.VIEW) {
            this.isLoading = true;
            this.variableService.getVariableById(this.variableId).pipe(finalize(() => this.isLoading = false)).toPromise().then(variableDetails => {
                this.variableDetails = structuredClone(variableDetails);
                this.obsoleteVariable = this.variableDetails?.obsolete ? ObsoleteEnum.YES : ObsoleteEnum.NO;
                this.showAliasField = this.variableDetails?.variableTypes.filter(variableType => this.isVariableTypeForAliasAllowed(variableType)).length > 0;
                this.showParenVariable = this.variableDetails?.scale?.dataType.name === DataTypeEnum.NUMERIC && this.variableDetails.variableTypes.filter(variableType => this.isVariableTypeAllowedForParentVariable(variableType)).length > 0;
                this.showRangeWidget = this.variableDetails?.scale?.dataType.name === DataTypeEnum.NUMERIC;
            });
        } else {
            this.showAliasField = false;
            this.variableDetails.obsolete = false;
            this.loadDropdowns();
            if (this.ontologyContext.variableDetails) {
                this.variableDetails = structuredClone(this.ontologyContext.variableDetails);
            } else {
                this.variableDetails = new VariableDetails();
                this.ontologyContext.variableDetails = structuredClone(this.variableDetails);
            }
        }
        this.isLoading = true;
        this.ontologyService.getNonSystemDataTypes().pipe(finalize(() => this.isLoading = false)).toPromise().then((dataTypes) => {
            this.dataTypes = dataTypes;
        });
    }

    isVariableTypeForAliasAllowed(variableType: VariableType) {
        return this.ALIAS_VARIABLE_ALLOWED.indexOf(variableType.id) > -1;
    }

    isVariableTypeAllowedForParentVariable(variableType: VariableType) {
        return this.ANALYSIS_VARIABLE_TYPE.indexOf(variableType.id) > -1;
    }

    edit() {
        this.variableDetailsBackup = structuredClone(this.variableDetails);
        this.loadDropdowns();
        this.modeView = ModeView.EDIT;
    }

    loadDropdowns() {
        this.isLoading = true;
        this.propertyService.getProperties().pipe(finalize(() => this.isLoading = false)).subscribe(properties => this.properties = properties);
        this.isLoading = true;
        this.methodService.getMethods().pipe(finalize(() => this.isLoading = false)).subscribe(methods => this.methods = methods);
        this.isLoading = true;
        this.scaleService.getScales().pipe(finalize(() => this.isLoading = false)).subscribe(scales => this.scales = scales);
        const excludeRestrictedTypes = true;
        this.isLoading = true;
        this.ontologyService.getVariableTypes(excludeRestrictedTypes).pipe(finalize(() => this.isLoading = false)).subscribe(variableTypes => this.variableTypes = variableTypes);
    }

    discard() {
        if (this.modeView === ModeView.CREATE) {
            this.clear();
        } else {
            this.modeView = ModeView.VIEW;
            this.variableDetails = Object.assign({}, this.variableDetailsBackup);
        }
    }

    clear() {
        this.activeModal.dismiss('cancel');
        this.ontologyContext.previousVariableRoute = null;
        this.ontologyContext.variableDetails = null;
    }

    back() {
        this.activeModal.dismiss('cancel');
        if (this.ontologyContext?.previousVariableRoute?.length > 0) {
            const variableIdBack = this.ontologyContext.previousVariableRoute.pop();
            setTimeout(() => {
                this.router.navigate(
                    ['/', { outlets: { popup: ['variables', variableIdBack, 'view'] } }],
                    { queryParamsHandling: 'merge' }
                );
            });
        } else {
            this.ontologyContext.previousVariableRoute = null;
        }
    }

    save() {
        this.isLoading = true;
        if (this.modeView === ModeView.EDIT) {
            this.variableService.update(this.variableDetails).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe(() => {
                this.alertService.success('ontology-manager.tab.variables.modal.variable.edited.success');
                this.notifyChanges();
            });
        } else {
            this.variableService.create(this.variableDetails).pipe(
                finalize(() => this.isLoading = false)).subscribe((res) => {
                if (this.variableDetails.favourite) {
                    this.isLoading = true;
                    this.programService.addProgramFavorite(
                        new ProgramFavoriteRequest(ProgramFavoriteTypeEnum.VARIABLE, [parseInt(res.body?.id, 10)]))
                        .pipe(finalize(() => {
                            this.isLoading = false;
                        })).subscribe();
                }
                this.alertService.success('ontology-manager.tab.variables.modal.variable.created.success');
                this.notifyChanges();
            });
        }
    }

    isFormValid(f) {
        const hasRequiredParentVariable = !this.showParenVariable || !!this.variableDetails?.parentVariable?.id;
        return f.form.valid
            && !this.isLoading
            && this.variableDetails?.name
            && this.variableDetails?.property
            && this.variableDetails?.method
            && this.variableDetails?.scale
            && this.variableDetails?.variableTypes?.length
            && hasRequiredParentVariable;
    }

    private resetWidgets() {
        this.showRangeWidget = this.showCategoricalWidget = false;
    }

    notifyChanges(): void {
        this.eventManager.broadcast({ name: 'variablePaneViewChanged' });
        this.clear();
    }

    protected showNoEditableFieldAlert() {
        return this.modeView === ModeView.EDIT && this.variableDetails?.metadata?.editableFields.length < this.NUM_EDITABLE_FIELDS;
    }

    protected onPropertyChanged($event: any) {
        this.variableDetails.property = this.properties.find(property => property.id === $event.id);
    }

    protected onMethodChanged($event: any) {
        this.variableDetails.method = this.methods.find(method => method.id === $event.id);
    }

    protected onScaleChanged($event: any) {
        this.variableDetails.scale = this.scales.find(scale => scale.id === $event.id);
        this.updateVariableTypeScaleCombination();
    }

    protected onObsoleteChanged() {
        this.variableDetails.obsolete = this.obsoleteVariable === 1 ? true : false;
    }

    selectVariable(variable: VariableDetails) {
        this.variableDetails.parentVariable = variable;
    }

    protected isObsoleteDisable() {
        return this.isLoading || this.modeView === ModeView.VIEW || !this.variableDetails?.metadata?.deletable;
    }

    protected onVariableTypeChanged($event: any) {
        const selectedVariableTypeIds = $event.map(variableType => variableType.id);
        this.variableDetails.variableTypes = this.variableTypes.filter(variableType => selectedVariableTypeIds.indexOf(variableType.id) > -1);
        this.updateVariableTypeScaleCombination();
    }

    private updateVariableTypeScaleCombination() {
        this.showAliasField = this.modeView !== ModeView.VIEW && this.variableDetails.variableTypes.filter(variableType => this.isVariableTypeForAliasAllowed(variableType)).length > 0;
        this.showParenVariable = this.variableDetails?.scale?.dataType?.name === DataTypeEnum.NUMERIC &&
            this.variableDetails?.variableTypes.filter(variableType => this.isVariableTypeAllowedForParentVariable(variableType)).length > 0;
        if (!!this.variableDetails?.scale && this.variableDetails?.variableTypes.filter(variableType => this.isVariableTypeAllowedForParentVariable(variableType)).length > 0) {
            this.invalidAnalysisScaleConbination = this.variableDetails?.scale?.dataType?.name !== DataTypeEnum.NUMERIC;
        }
        this.showRangeWidget = this.variableDetails?.scale?.dataType?.name === DataTypeEnum.NUMERIC;
    }

    isDeletable() {
        return this.variableDetails?.metadata?.deletable;
    }

    isProgramFavorite(): boolean {
        return this.variableDetails?.favourite;
    }

    toggleProgramFavorite() {
        // Prevent multiple click while a program favorite is being added/removed
        if (this.isTogglingFavoriteProgram) {
            return;
        }

        this.variableDetails.favourite = !this.variableDetails.favourite;

        if (this.modeView !== ModeView.CREATE) {
            this.isLoading = true;
            this.isTogglingFavoriteProgram = true;
            if (this.variableDetails.favourite) {
                this.programService.addProgramFavorite(
                    new ProgramFavoriteRequest(ProgramFavoriteTypeEnum.VARIABLE, [parseInt(this.variableDetails.id, 10)]))
                    .pipe(finalize(() => {
                        this.isTogglingFavoriteProgram = false;
                        this.isLoading = false;
                    })).subscribe();
            } else {
                this.programService.getProgramFavorite(ProgramFavoriteTypeEnum.VARIABLE, parseInt(this.variableDetails.id, 10)).pipe(finalize(() => {
                    this.isTogglingFavoriteProgram = false;
                    this.isLoading = false;
                })).subscribe((res: HttpResponse<ProgramFavorite>) => {
                    if (res?.body?.programFavoriteId) {
                        this.isTogglingFavoriteProgram = true;
                        this.isLoading = true;
                        this.programService.removeProgramFavorite([res.body.programFavoriteId])
                            .pipe(finalize(() => {
                                this.isTogglingFavoriteProgram = false;
                                this.isLoading = false;
                            })).subscribe();
                    }
                });
            }
        }
    }

    addNewProperty() {
        this.activeModal.dismiss('cancel');
        this.ontologyContext.variableDetails = structuredClone(this.variableDetails);
        setTimeout(() => {
            this.router.navigate(
                ['/', { outlets: { popup: ['properties', 'new'] } }],
                { queryParamsHandling: 'merge' }
            );
        });
    }

    addNewMethod() {
        this.activeModal.dismiss('cancel');
        this.ontologyContext.variableDetails = structuredClone(this.variableDetails);
        setTimeout(() => {
            this.router.navigate(
                ['/', { outlets: { popup: ['methods', 'new'] } }],
                { queryParamsHandling: 'merge' }
            );
        });
    }

    addNewScale() {
        this.activeModal.dismiss('cancel');
        this.ontologyContext.variableDetails = structuredClone(this.variableDetails);
        setTimeout(() => {
            this.router.navigate(
                ['/', { outlets: { popup: ['scales', 'new'] } }],
                { queryParamsHandling: 'merge' }
            );
        });
    }

    protected delete() {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        const message = 'ontology-manager.variable.confirm.deletion';
        confirmModalRef.componentInstance.message = this.translateService.instant(message);
        confirmModalRef.result.then(() => {
            this.isLoading = true;
            this.variableService.delete(this.variableDetails.id).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe(() => {
                this.alertService.success('ontology-manager.tab.variables.modal.variable.deleted.success');
                this.notifyChanges();
            });
        });
    }

    protected filterbyVariableType() {
        this.eventManager.broadcast({
            name: 'variablePaneViewChanged', content: {
                filterBy: 'variableTypeIds', values: this.variableDetails.variableTypes.map((variableType) => {
                    return {
                        id: variableType.id,
                        text: variableType.name
                    };
                })
            }
        });
        this.clear();
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapePressed(event: KeyboardEvent): void {
        event.preventDefault();
        this.clear();

    }
}

@Component({
    selector: 'jhi-variable-popup',
    template: ''
})
export class VariablePopupComponent implements OnInit {

    constructor(private popupService: PopupService,
                private route: ActivatedRoute) {
    }

    ngOnInit(): void {
        const id = this.route.snapshot.params['id'];
        const modalRef = this.popupService.open(VariableDialogComponent as Component, {
            modalDialogClass: 'modal-lg',
            backdrop: 'static'
        });
        modalRef?.then(ngmodalRef => ngmodalRef.componentInstance.variableId = id ? Number(id) : null);
    }

}

export enum ObsoleteEnum {
    YES = 1,
    NO = 0
}
